import { WSElement } from "@wingspanhq/fe-component-library";
import React from "react";
import meridioHorizontalLogo from "../assets/MeridioHorizontalLogo.png";
import meridioSmallLogo from "../assets/MeridioSmallLogo.png";
import bcmgLogo from "../assets/axis/bcmg-logo.png";
import lloydCapitalLogo from "../assets/axis/lloyd-capital-logo.png";
import manhattanLifeLogo from "../assets/axis/manhattan-life-logo.png";
import mutualOfOmahaLogo from "../assets/axis/mutual-of-omaha-logo.png";
import mutualTrustLogo from "../assets/axis/mutual-trust-logo.png";
import transamericaLogo from "../assets/axis/transamerica-logo.png";
import unitedHealthcareLogo from "../assets/axis/united-healthcare-logo.png";
import { MeridioCTA } from "../components/customBenefits/Meridio/MeridioCTA";
import {
  MERIDIO_KEY,
  OAI_KEY,
  PGLI_KEY,
  PLAN_DETAILS
} from "../utils/constants";
import { Benefit, BenefitCategory, BenefitGate } from "./types";

const oai = PLAN_DETAILS[OAI_KEY];
const pgli = PLAN_DETAILS[PGLI_KEY];

/** Logos supplied by Axis are wordmarks and need more room than a square badge. */
const AXIS_LOGO_WIDTH = 100;

const AXIS_CONTACT = {
  sidebarContactDescription: "Contact",
  sidebarContactEmail: "Support@axis1099.com",
  sidebarContactPhone: "517-899-0154"
} as const;

/**
 * The entire benefits catalog.
 *
 * Previously split across the Webflow CMS collection and four hardcoded
 * sources (Meridio, OAI, PGLI and the Axis partner list). Gating that used to
 * live in `selectorBenefitsList` is expressed here via `gate`.
 */
export const BENEFITS: Benefit[] = [
  {
    slug: "tax-hotline",
    name: "Tax Hotline",
    category: BenefitCategory.Legal,
    atAGlance:
      "Free tax return preparation and year-round support from tax experts whenever you need it.",
    mainHeader: "Tax Hotline",
    videoUrl:
      "https://player.vimeo.com/video/511752661?badge=0&autopause=0&player_id=0&app_id=58479",
    mainDescription:
      "Free tax return preparation and year-round support from tax experts.",
    mainDescriptionList: [
      "Advice from tax attorneys, financial analysts, CPA's, former auditors and/or Enrolled Agents certified by the IRS",
      "Unlimited advice on federal taxes",
      "Free tax return preparation for 1040EZ, 1040A and 1040 and review of last year’s return (limit one per household)",
      "Discounts on other tax forms and schedules",
      "Access to member portal with tax tips, tax law changes, advice and more"
    ],
    mainLearnMoreLink:
      "https://help.wingspan.app/en/collections/5847687-lifestyle-benefits",
    sidebarCtaLink: "https://www.taxhotline.net/",
    sidebarCtaText: "Visit resource center",
    sidebarContactPhone: "(866) 544-4399"
  },

  {
    slug: "prescription-savings",
    name: "Prescription Savings",
    category: BenefitCategory.Health,
    atAGlance:
      "Save an average of 34% to 79% on prescriptions for your family and pets including home delivery.",
    mainHeader: "Prescription Savings",
    mainSubheader: "Powered by New Benefits Rx & Express Scripts",
    videoUrl:
      "https://player.vimeo.com/video/518277189?badge=0&autopause=0&player_id=0&app_id=58479",
    mainDescription:
      "Save an average of 34% to 79% on most brand and generic medications at retail and home delivery pharmacies. Coverage extends to family, and even pets.",
    mainDescriptionList: [
      "Receive discounts on thousands of medications at 60,000+ retail pharmacies nationwide, including CVS, Walmart, and Walgreens. Save time and money through home delivery, powered by Express Scripts, delivering discounted medication directly to your door with free shipping.",
      "Get discounts on prescriptions for your pets",
      "Find the best deal: compare prescription prices at participating pharmacies through your mobile app or web portal"
    ],
    mainLearnMoreLink:
      "https://help.wingspan.app/en/articles/4716746-what-is-prescription-savings-and-how-does-it-work"
  },

  {
    slug: "alternative-medicine",
    name: "Alternative Medicine",
    category: BenefitCategory.Wellness,
    atAGlance:
      "Save 10% to 30% on your wellness services like Acupuncture, Massage Therapy, Nutritional Counseling, Hypnotherapy, Yoga, Tai Chi, Pilates, Reflexology and more.",
    logoUrl:
      "https://uploads-ssl.webflow.com/6143539d86d8a27256ea08db/619bf82a37e9d4d6f0e8a5eb_60c546afb14d274099ef259c_Tivity.png",
    mainHeader: "Alternative Medicine",
    videoUrl:
      "https://player.vimeo.com/video/512691898?badge=0&autopause=0&player_id=0&app_id=58479",
    mainDescription:
      "Save 10% to 30% on your wellness services like Acupuncture, Massage Therapy, Nutritional Counseling, Hypnotherapy, Yoga, Tai Chi, Pilates, Reflexology and more.",
    mainDescriptionList: [
      "Over 35 specialty services and 35,000 practitioners nationwide",
      "Discounts on services like: Acupuncture, Massage Therapy, Nutritional Counseling, Biofeedback, Hypnotherapy, Reflexology, Yoga, Tai Chi and Pilates"
    ],
    sidebarCtaLink: "https://mybenefitswork.com/login",
    sidebarCtaText: "Launch in My Benefits Work"
  },

  {
    slug: "chiropractic",
    name: "Chiropractic",
    category: BenefitCategory.Wellness,
    atAGlance:
      "Save 30% to 50% on X-rays, diagnostic services and treatments at chiropractors nationwide.",
    logoUrl:
      "https://uploads-ssl.webflow.com/6143539d86d8a27256ea08db/619bf8f40ade223ebc28a354_60c545de843ba14a9effcaa5_UHS.svg",
    mainHeader: "Chiropractic",
    videoUrl:
      "https://player.vimeo.com/video/511154551?badge=0&autopause=0&player_id=0&app_id=58479",
    mainDescription:
      "Costing an estimated $90 billion per year, lower back pain is the second leading cause of disability in the U.S., affecting more than 80% of the population at some point in their lives. Research points to chiropractic care as a safe, effective treatment for back pain without reliance on pricey pain medications, but it isn’t well-covered by most insurance plans. Discounted chiropractic care gives employees affordable access to participating chiropractors for relief from back and joint pain, headaches, and more with no limit on the number of visits."
  },

  {
    slug: "dental-insurance",
    name: "Dental Insurance",
    category: BenefitCategory.Insurance,
    orderPriority: 0,
    isAdditionalCoverage: true,
    atAGlance: "A dental program that stays with you!",
    logoUrl:
      "https://uploads-ssl.webflow.com/6143539d86d8a27256ea08db/619bf8f5efc1af07484b8fc8_61403c3643748a672cbf4866_MetLifeLogo.jpeg",
    costDescription: "Varied",
    costIsNotIncluded: true,
    mainHeader: "Dental Insurance",
    mainSubheader:
      "Smile. Now you can have dental that you can take with you. MetLife TakeAlong Dental",
    mainDescription:
      "Introducing MetLife TakeAlong Dental. TakeAlong Dental offers an individual dental policy or a non-insurance discount dental program. In either case, you can take these benefits along with you through every life event you experience. So this could be the last time you’ll have to enroll for dental. Now there’s something to make you smile!",
    mainLearnMoreLink:
      "https://www.metlifetakealongdental.com/?ReferralCode=Wingspan",
    sidebarLogoUrl:
      "https://uploads-ssl.webflow.com/6143539d86d8a27256ea08db/619bf8f5efc1af07484b8fc8_61403c3643748a672cbf4866_MetLifeLogo.jpeg",
    sidebarHideMemberIds: true,
    sidebarCtaLink:
      "https://www.metlifetakealongdental.com/?ReferralCode=Wingspan",
    sidebarCtaText: "Get started"
  },

  {
    slug: "dental-savings",
    name: "Dental Savings",
    category: BenefitCategory.Health,
    atAGlance: "15% to 50% savings",
    logoUrl:
      "https://uploads-ssl.webflow.com/6143539d86d8a27256ea08db/619bf82cefc1af352d4b866f_60c546608684b31030a60b5a_Aetna.jpeg",
    mainHeader: "Dental Savings",
    mainSubheader: "Powered by Aetna Dental Access®",
    videoUrl:
      "https://player.vimeo.com/video/513595255?badge=0&autopause=0&player_id=0&app_id=58479",
    mainDescription:
      "Smile brighter with big savings on dental services such as cleanings, X-rays and crowns. In most instances, save 15% to 50% per visit* on services from general dentistry to special procedures. Use your card over and over again to keep your teeth sparkling clean!",
    mainDescriptionList: [
      "262,000** available dental practice locations nationwide",
      "In most instances, save 15% to 50% per visit*",
      "Includes specialty dental care- save on orthodontics and periodontics, too!"
    ],
    mainFootnote:
      "*Actual costs and savings vary by provider, service and geographical area. **As of April 2020.",
    sidebarCtaLink: "https://mybenefitswork.com/login",
    sidebarCtaText: "Launch in My Benefits Work"
  },

  {
    slug: "diabetic-supplies",
    name: "Diabetic Supplies",
    category: BenefitCategory.Health,
    atAGlance:
      "Save 10% to 50% on diabetic testing supplies mailed directly to your home.",
    mainHeader: "Diabetic Supplies",
    mainSubheader: "Powered by HealthSherpa",
    videoUrl:
      "https://player.vimeo.com/video/511166349?badge=0&autopause=0&player_id=0&app_id=58479",
    mainDescription:
      "Save 10% to 50% on diabetic testing supplies mailed directly to your home.",
    mainDescriptionList: [
      "Get a free fully-audible blood glucose meter with your first order",
      "Free shipping on diabetic supply packages",
      "Annual testing program or purchase-as-needed options available",
      "No prescription required for OTC testing supplies",
      "100% satisfaction guaranteed, or get your money back"
    ]
  },

  {
    slug: "fertility-advocacy",
    name: "Fertility Advocacy",
    category: BenefitCategory.Wellness,
    atAGlance:
      "Guided support for every step of your fertility treatment journey, providing education, resources, discounts and emotional support.",
    logoUrl:
      "https://uploads-ssl.webflow.com/6143539d86d8a27256ea08db/619bf82e775fd37d2b6dbb88_60c54699d3399e15d12286b9_WIN.svg",
    mainHeader: "Fertility Advocacy",
    mainSubheader: "Powered by WINFertility",
    videoUrl:
      "https://player.vimeo.com/video/512559227?badge=0&autopause=0&player_id=0&app_id=58479",
    mainDescription:
      "Fertility Advocates guide you every step of the way through your fertility treatment journey, providing education, resources, discounts on medical services, and emotional support for your individual needs.",
    mainDescriptionList: [
      "24/7 access to WIN’s Nurse Care Manager",
      "Access to a network of top-ranked fertility specialists, genetic testing and egg freezing services",
      "IVF treatment and Medication Bundles℠",
      "Behavioral Health Care Managers provide counseling on grief associated with infertility, managing stress, and more",
      "Complimentary prenatal package",
      "Discounted medical and Rx options",
      "Financing opportunities",
      "Convenient mobile app to discreetly track fertility, find an in-network doctor, and connect with a WIN nurse"
    ],
    mainLearnMoreLink:
      "https://help.wingspan.app/en/articles/4716649-what-is-fertility-advocacy",
    sidebarCtaLink: "https://managed.winfertility.com/new-benefits/",
    sidebarCtaText: "Visit portal",
    sidebarContactPhone: "(866) 744-8088"
  },

  {
    slug: "financial-helpline",
    name: "Financial Helpline",
    category: BenefitCategory.Legal,
    atAGlance:
      "Take on major life changes with live one-on-one coaching from accredited financial counselors.",
    mainHeader: "Financial Helpline",
    mainSubheader: "Powered by Financial Wellness Center",
    videoUrl:
      "https://player.vimeo.com/video/512684588?badge=0&autopause=0&player_id=0&app_id=58479",
    mainDescription:
      "Achieve financial wellness and take on major life changes with live one-on-one coaching from accredited financial counselors*.",
    mainDescriptionList: [
      "Receive one-on-one coaching or tackle your financial situation independently with learning tracks through the Online Financial Wellness Center",
      "Accredited or Certified Financial Counselors are accessible by phone to assess issues, discuss options, and help you determine the best course of action for your situation.",
      "Get resources and support for major life events, like getting married or having a child, and general financial goals, like developing a budget and eliminating student debt"
    ],
    mainFootnote:
      "*Financial Wellness does not provide investment, legal, or tax advice.",
    sidebarCtaLink: "https://www.askfinancialwellness.info/nbi-login-access",
    sidebarCtaText: "Visit Financial Wellness Center",
    sidebarContactPhone: "(800) 704-1308"
  },

  {
    slug: "healthcare-support",
    name: "Healthcare Support",
    category: BenefitCategory.Health,
    orderPriority: 1,
    atAGlance:
      "Help with finding the right provider, coordinating care, and even negotiating bills.",
    logoUrl:
      "https://uploads-ssl.webflow.com/6143539d86d8a27256ea08db/619bf82eb91174a0f9c6d267_60c3488a8518d0d07b025ec3_HealthAdvocate.png",
    mainHeader: "Healthcare Support",
    mainSubheader:
      "Health Advocate is the nation’s largest healthcare advocacy network. As a Wingspan member, enjoy these services free of charge for you and your family",
    videoUrl:
      "https://player.vimeo.com/video/511761051?badge=0&autopause=0&player_id=0&app_id=58479",
    mainDescription:
      "Healthcare is becoming harder to understand. Get one-on-one support from professionals and registered nurses for billing and insurance-related issues to help you confidently navigate the healthcare system.",
    mainCards: [
      {
        lines: ["Answer questions about plans, claims, and bills"]
      },
      {
        lines: ["Resolve claims, find doctors, and research treatments"]
      },
      {
        lines: ["Coordinate care and book appointments"]
      },
      {
        lines: ["Connect you with registered nurses 24/7"]
      },
      {
        lines: ["Assist with elder care and related health issues"]
      },
      {
        lines: ["Extend services to the whole family, (your parents, too)"]
      }
    ],
    mainLearnMoreLink:
      "https://help.wingspan.app/en/collections/5847687-lifestyle-benefits",
    mainLearnMoreLinkText: "Learn more about Health Advocate",
    sidebarHeader: "Get support today!",
    sidebarLogoUrl:
      "https://uploads-ssl.webflow.com/6143539d86d8a27256ea08db/619bf82eb911744374c6d266_60c3489e510813219019475e_HealthAdvocateLogo.svg",
    sidebarUsageText:
      "Activate your Health Advocate benefit now using your membership ID: {{membershipId}}",
    sidebarHideMemberIds: true,
    sidebarCtaLink:
      "https://members.healthadvocate.com/Account/OrganizationSearch",
    sidebarCtaText: "Get started",
    sidebarContactDescription:
      "With 24/7 support, you can call or email anytime. Just say your name, Wingspan, and why you’re calling to get connected to the service that’s right for you.",
    sidebarContactEmail: "answers@healthadvocate.com",
    sidebarContactPhone: "(866) 272-6009"
  },

  {
    slug: "hearing-aids",
    name: "Hearing Aids",
    category: BenefitCategory.Health,
    orderPriority: 1,
    atAGlance:
      "Support finding the right hearing aid solution to fit your lifestyle through personalized service and products for every budget.",
    mainHeader: "Hearing Aids",
    mainSubheader: "Powered by Amplifon",
    videoUrl:
      "https://player.vimeo.com/video/540249813?badge=0&autopause=0&player_id=0&app_id=58479",
    mainDescription:
      "Amplifon helps you find the right hearing aid solution to fit your lifestyle through personalized service and exceptional products for every budget",
    mainDescriptionList: [
      "One year free follow-up care for cleaning and checkups with purchase",
      "Two-year supply of hearing aid batteries and three-year warranty",
      "Offering leading manufacturers including Siemens, Rexton, Miracle-Ear, Phonak, ReSound, Unitron, Oticon, Sonic Innovations, Starkey, and Widex"
    ]
  },

  {
    slug: "lab-testing",
    name: "Lab Testing",
    category: BenefitCategory.Health,
    atAGlance:
      "Monitor your health with 10% to 80% off typical costs of routine lab work.",
    mainHeader: "Lab Testing",
    mainSubheader: "Powered by DirectLabs",
    videoUrl:
      "https://player.vimeo.com/video/522495080?badge=0&autopause=0&player_id=0&app_id=58479",
    mainDescription:
      "Monitor your health with 10% to 80% off typical costs of routine lab work*.",
    mainDescriptionList: [
      "Direct access to 1500 clinical laboratories nationwide where you can save 10% to 80% on typical costs for lab work.",
      "Test categories include allergies, vitamin and cholesterol levels, liver function, fertility, thyroid and more",
      "Confidential results in as little as 24 hours for most tests"
    ],
    mainFootnote: "*Lab benefit not available in MD, NJ, NY and RI."
  },

  {
    slug: "legal-access",
    name: "Legal Access",
    category: BenefitCategory.Legal,
    atAGlance:
      "Have legal questions? Get legal support with free and discounted services from experienced lawyers.",
    logoUrl:
      "https://uploads-ssl.webflow.com/6143539d86d8a27256ea08db/619bf82f269d9964c7b718a3_60c546bd2d9aaf0c0cf15e3b_LegalClub.png",
    mainHeader: "Legal Access",
    videoUrl:
      "https://player.vimeo.com/video/509917103?badge=0&autopause=0&player_id=0&app_id=58479",
    mainDescription:
      "Have legal questions? Get legal support with free and discounted services from experienced lawyers.",
    mainDescriptionList: [
      "Free services* include one-on-one consultations, attorney-made phone calls, help with legal documents, assistance with government programs, representation in small claims court, and a Simple and Living Will",
      "Attorneys help with traffic tickets, bankruptcy, divorce, and spousal and child support",
      "Attorneys charge 25% off their hourly rate for services like",
      "Available for your spouse, dependents up to age 26, your parents and your spouse’s parents",
      "Referrals to plan attorneys based on location, language and area of law"
    ],
    mainFootnote:
      "*In many states, attorney liability may require plan attorneys to obtain a retainer from the member prior to providing some of the free member benefits.",
    sidebarContactPhone: "(877) 856-5547"
  },

  {
    slug: "life-insurance",
    name: "Life Insurance",
    category: BenefitCategory.Insurance,
    orderPriority: 3,
    isAdditionalCoverage: true,
    atAGlance: "Simple, affordable life insurance.",
    logoUrl:
      "https://uploads-ssl.webflow.com/6143539d86d8a27256ea08db/619bf82f775fd358236dbb8c_61403fcbfcc358435bb22295_ethos-life-logo.png",
    costDescription:
      "Just $50 a month can get your family $700,000 in life insurance coverage.",
    costIsNotIncluded: true,
    mainHeader: "Life Insurance",
    mainDescription:
      "No medical exams or blood tests, just answer a few health questions online. Get same-day coverage on affordable policies from our top-rated carriers in minutes.",
    mainInfoListHeader: "How it works",
    mainInfoListSteps: [
      {
        title: "See your options",
        description: [
          "Before you apply, get an instant, personalized quote with multiple coverage plans to choose from."
        ]
      },
      {
        title: "Apply in minutes",
        description: [
          "Apply online after you choose your plan. It’s simple, straightforward, and only takes about 10 minutes."
        ]
      },
      {
        title: "Get covered",
        description: [
          "You’ll receive a final decision once our underwriters have reviewed your application. Our licensed agents will reach out and help activate your policy."
        ]
      }
    ],
    mainFaqsHeader: "FAQs",
    mainFaqs: [
      {
        question: "What does life insurance cover?",
        answer: [
          "Your policy can help your loved ones in their greatest time of need. It can help cover some of life’s biggest expenses, like a home mortgage, debt, your children’s college tuition, and it can also replace lost income. Your policy can also help cover everyday expenses — anything your beneficiaries need, really. Ultimately, it’s up to them to decide how to use the payout."
        ]
      },
      {
        question: "How much life insurance do I need?",
        answer: [
          "A common and easy way to come up with a coverage estimate is to multiply your annual income by 10. Another way is to calculate your long-term financial obligations and then subtract your assets. The remainder is the gap that life insurance needs to fill."
        ]
      },
      {
        question: "Does coronavirus impact life insurance?",
        answer: [
          "Life insurance does cover deaths related to COVID-19, and life insurance rates are not impacted by the pandemic. We’re not treating this any differently than we would any other illness or travel advisory. You can still apply for life insurance online with Ethos. If your application is approved, you’ll be considered covered as soon as your first premium payment is made. Learn more"
        ]
      },
      {
        question: "What if I have a policy through my employer?",
        answer: [
          "You may already have some life insurance coverage from your employer. However, that policy might only provide a fraction of the coverage you actually need. Employer-sponsored policies typically offer coverage that is about 1-2× your annual salary. However, financial experts recommend having coverage that is about 10× your salary. This disparity can result in a large gap in protection if you’re solely relying on your policy through work — which is why many people buy individual term policies to supplement the coverage you receive through work."
        ]
      }
    ],
    mainLearnMoreLink: "https://agents.ethoslife.com/invite/8974b",
    mainLearnMoreLinkText: "Get covered",
    sidebarLogoUrl:
      "https://uploads-ssl.webflow.com/6143539d86d8a27256ea08db/619bf82f775fd358236dbb8c_61403fcbfcc358435bb22295_ethos-life-logo.png",
    sidebarHideMemberIds: true,
    sidebarCtaLink: "https://agents.ethoslife.com/invite/8974b",
    sidebarCtaText: "Get covered"
  },

  {
    slug: "mri-ct-scans",
    name: "MRI & CT Scans",
    category: BenefitCategory.Health,
    atAGlance:
      "Save 40% to 75% on usual charges for MRI, CT Scans and more at thousands of credentialed radiology centers nationwide.",
    mainHeader: "MRI & CT Scans",
    mainSubheader: "Powered by One Call Medical",
    videoUrl:
      "https://player.vimeo.com/video/530934994?badge=0&autopause=0&player_id=0&app_id=58479",
    mainDescriptionList: [
      "Save 40% to 75% on usual charges for MRI and CT Scans at thousands of credentialed radiology centers nationwide",
      "Referrals to a certified radiologist based on condition, preferences and location"
    ]
  },

  {
    slug: "pet-care",
    name: "Pet Care",
    category: BenefitCategory.Other,
    orderPriority: 4,
    atAGlance:
      "Keep your pets happy and healthy with discounts on everything from toys and treats to boarding and eats!",
    logoUrl:
      "https://cdn.prod.website-files.com/6143539d86d8a27256ea08db/619bf830775fd3a5da6dbb8d_60c5467a507914d9476cb6ff_Pet.png",
    mainHeader: "Pet Care",
    videoUrl:
      "https://player.vimeo.com/video/512667311?badge=0&autopause=0&player_id=0&app_id=58479",
    mainDescription:
      "Keep your pets happy and healthy with discounts on everything from toys and treats to boarding and eats!",
    mainDescriptionList: [
      "25% off all in-house medical services at participating veterinarians",
      "$20 credit to Rover.com for boarding, sitting and walking (new users only)",
      "25% off all purchases from PetCareRx.com, including prescriptions and preventatives",
      "24/7 Lost Pet Recovery Service, with pet tags for each enrolled pet",
      "35% off monthly Pin Paws membership with GPS-enabled lost pet notification system"
    ],
    sidebarCtaLink: "https://mybenefitswork.com/login",
    sidebarCtaText: "Launch in My Benefits Work"
  },

  {
    slug: "physician-hospital-discounts",
    name: "Physician & Hospital Discounts",
    category: BenefitCategory.Health,
    atAGlance:
      "Save 10% to 40% on most medical services at over 340,000 participating facilities.",
    mainHeader: "Physician & Hospital Discounts",
    mainSubheader: "Powered by ValuePoint",
    videoUrl:
      "https://player.vimeo.com/video/511745224?badge=0&autopause=0&player_id=0&app_id=58479",
    mainDescription:
      "Save 10% to 40% on most medical services at over 340,000 participating physicians, ancillary facilities, and hospitals.",
    mainDescriptionList: [
      "Includes family doctors, specialists, alternative care providers, facilities and hospitals",
      "No health restrictions, age limits, or usage/savings maximums",
      "National savings average 37% at doctor offices, 28% at ancillary facilities, and 22-24% at hospitals"
    ]
  },

  {
    slug: "vitamins",
    name: "Vitamins",
    category: BenefitCategory.Wellness,
    atAGlance:
      "Vitacost helps you find the best prices online for the most trusted brands of vitamins and wellness products.",
    logoUrl:
      "https://uploads-ssl.webflow.com/6143539d86d8a27256ea08db/619bf833199c96599c563f99_60c545fa10556d0b5be16379_Vitacost.png",
    mainHeader: "Vitamins",
    mainSubheader: "Powered by Vitacost",
    videoUrl:
      "https://player.vimeo.com/video/511742471?badge=0&autopause=0&player_id=0&app_id=58479",
    mainDescription:
      "Vitacost helps you find the best prices online for the most trusted brands of vitamins and wellness products.",
    mainDescriptionList: [
      "Take an additional 10% off already low prices on products for you, your family and even your pets",
      "Use the mobile app or order online",
      "30-day money-back guarantee",
      "Get discounts on everything from natural household supplies and baby care essentials to skin care products and gluten free foods"
    ]
  },

  {
    slug: "vision-insurance",
    name: "Vision Insurance",
    category: BenefitCategory.Insurance,
    orderPriority: 2,
    isAdditionalCoverage: true,
    atAGlance: "Vision coverage for lower out-of-pocket costs",
    logoUrl:
      "https://uploads-ssl.webflow.com/6143539d86d8a27256ea08db/619bf83385a3c9dd4dd1a2ac_6140381c8120b9498c8dc1cc_VSP-Logo.jpeg",
    costDescription: "Cost: Plans as low as $17 per month",
    costIsNotIncluded: true,
    mainHeader: "Vision Insurance",
    mainCards: [
      {
        lines: [
          "Quality Care You Deserve",
          "You deserve a WellVision Exam®—a comprehensive eye exam that can help detect early signs of health conditions, like",
          "diabetes. And with about five VSP® network doctors within six miles of you, it’s easy to find an in-network doctor."
        ]
      },
      {
        lines: [
          "Styles You'll Love",
          "Find hundreds of frame options to choose from and get an extra $20 to spend on featured frame brands like bebe, CALVIN KLEIN, Cole Haan, Flexon®, Lacoste, Nike, Nine West, and more. Plus, save up to 30% on lens enhancements!"
        ]
      },
      {
        lines: [
          "Savings You Expect",
          "You’ll get low out-of-pocket costs on your vision care essentials, including access to member-exclusive offers and savings. Typical annual",
          "savings are over $200."
        ]
      }
    ],
    mainInfoListHeader: "Using Your VSP Benefit is Easy",
    mainInfoListSteps: [
      {
        title:
          "Create an account at https://www.vspdirect.com/wingspan/welcome to see your plan information."
      },
      {
        title:
          "To find an in-network doctor based on your plan-type, visit vsp.com or call 800.877.7195. Maximize your coverage with bonus offers and savings that are exclusive to Premier Program locations—including thousands of private practice doctors and more than 700 Visionworks retail locations nationwide."
      },
      {
        title: "At your appointment, tell them you have VSP."
      },
      {
        title:
          "VSP and your doctor will handle the rest—there are no claim forms to complete."
      }
    ],
    mainLearnMoreLink: "https://www.vspdirect.com/wingspan/find-a-doctor",
    mainLearnMoreLinkText: "Find a doctor",
    sidebarLogoUrl:
      "https://uploads-ssl.webflow.com/6143539d86d8a27256ea08db/619bf83385a3c9dd4dd1a2ac_6140381c8120b9498c8dc1cc_VSP-Logo.jpeg",
    sidebarHideMemberIds: true,
    sidebarCtaLink: "https://www.vspdirect.com/wingspan/welcome",
    sidebarCtaText: "See plans"
  },

  {
    slug: "virtual-care",
    name: "Virtual Care",
    category: BenefitCategory.Health,
    orderPriority: 1,
    atAGlance:
      "See a doctor from the comfort of your own home via live video chat. Available 24/7.",
    logoUrl:
      "https://uploads-ssl.webflow.com/6143539d86d8a27256ea08db/619bf8321409587e3a148124_60c348346ab39e958f08c706_Teladoc.png",
    costDescription: "$0 per visit",
    mainHeader: "Virtual Care",
    videoUrl: "https://player.vimeo.com/video/354315243",
    mainDescription:
      "Teladoc helps Wingspan members access on-demand healthcare via phone or video. A physician is always just a call, click, or swipe away. Here’s how Teladoc can help:",
    mainCards: [
      {
        lines: ["Quality care in minutes"]
      },
      {
        lines: ["On-the-go access to healthcare"]
      },
      {
        lines: ["Less visits to the ER and Urgent Care"]
      }
    ],
    mainFaqs: [
      {
        question: "Can my family use Teladoc?",
        answer: [
          "All of your family members have acccess to Teladoc. They can sign up using your ID."
        ]
      },
      {
        question: "What are some of the common conditions Teladoc treats?",
        answer: [
          "Common conditions include sinus problems, respiratory infection, allergies, urinary tract infection, cold and flu symptoms and many other non-emergency illnesses."
        ]
      }
    ],
    mainLearnMoreLink:
      "https://help.wingspan.app/en/collections/5847687-lifestyle-benefits",
    mainLearnMoreLinkText: "Learn more about Virtual Care powered by Teladoc",
    sidebarHeader: "Talk with a physician today!",
    sidebarLogoUrl:
      "https://uploads-ssl.webflow.com/6143539d86d8a27256ea08db/619bf832140958377e148125_60c34961ef66f5bbd29b1f04_TeladocLogo.svg",
    sidebarUsageText:
      "Activate your Teladoc benefit now using your membership ID: {{membershipId}}",
    sidebarHideMemberIds: true,
    sidebarCtaLink: "https://mybenefitswork.com/login",
    sidebarCtaText: "Speak to a doctor now"
  },

  {
    slug: "vision-savings",
    name: "Vision Savings",
    category: BenefitCategory.Health,
    atAGlance: "10% to 60% savings",
    logoUrl:
      "https://uploads-ssl.webflow.com/6143539d86d8a27256ea08db/619bf833269d9994d8b718a5_60c5464d3c40c404f54db892_CTCV.png",
    mainHeader: "Vision Savings",
    mainSubheader: "Powered by Coast To Coast Vision™",
    videoUrl:
      "https://player.vimeo.com/video/512562359?badge=0&autopause=0&player_id=0&app_id=58479",
    mainDescription:
      "Save 10% to 60% on glasses, contacts, laser surgery, exams and more.",
    mainDescriptionList: [
      "20% to 60% off prescription eyewear including most frames, lenses and specialty items such as tints, coatings and UV protection",
      "10% to 30% off eye exams",
      "Participating chains include LensCrafters, Pearle Vision, Visionworks, JCPenney, Target and more",
      "Nominate your eye doctor to move in network",
      "10% to 40% off contact lenses",
      "40% to 50% off the national average cost of LASIK surgery"
    ],
    mainLearnMoreLink:
      "https://help.wingspan.app/en/articles/4718573-what-is-vision-savings",
    sidebarCtaLink: "https://mybenefitswork.com/login",
    sidebarCtaText: "Launch in My Benefits Work"
  },

  {
    slug: "assurely-business-insurance",
    name: "Business Insurance",
    category: BenefitCategory.Insurance,
    orderPriority: 99,
    isAdditionalCoverage: true,
    atAGlance: "Simple, industry-leading, cost-effective business insurance.",
    logoUrl:
      "https://cdn.prod.website-files.com/6143539d86d8a27256ea08db/68c0496e973f6a3ef9bec065_Assurely%20an%20Equal%20Parts%20Company%20(3).png",
    costDescription: "Varied",
    costIsNotIncluded: true,
    mainHeader: "Business Insurance",
    mainSubheader:
      "Simple, industry-leading, cost-effective business insurance.",
    mainDescription:
      "Wingspan has partnered with Assurely, an Equal Parts Company, to simplify insurance for members and provide them with a one-stop insurance platform. Via an easy-to-use insurance dashboard all business insurance needs – E&O, GL, BOP, cyber, property, auto etc. – can be purchased, managed, organized and renewed through the Assurely platform and/or with the help of Assurely’s dedicated insurance experts.",
    mainCards: [
      {
        lines: ["Satisfy your insurance requirements"]
      },
      {
        lines: ["All-in-one insurance dashboard"]
      },
      {
        lines: ["Dedicated, VIP customer support"]
      }
    ],
    mainInfoListHeader: "How it works",
    mainInfoListSteps: [
      {
        title: "Log into your premade account (under 1 minute)",
        description: [
          "Since you have a Wingspan account, your account with Assurely is pre-populated with essential details about your business and insurance requirements."
        ]
      },
      {
        title: "Request insurance coverage (2-5 minutes)",
        description: [
          "Choose from a list of options what insurance you are looking for. Assurely’s dedicated insurance advisors will collect multiple quotes from top insurance carriers to ensure you are getting the best coverage for the best price available."
        ]
      },
      {
        title: "Get covered (2-5 minutes)",
        description: [
          "Once your quotes are ready, you will receive an email prompting you to log into the Assurely platform to view your quotes along with an expert recommendation on which is best for your business and to purchase your coverage."
        ]
      }
    ],
    mainFaqs: [
      {
        question: "Who is Equal Parts?",
        answer: [
          "Equal Parts is a tech-enabled insurance brokerage and parent company of World Trade Labs Inc., DBA Assurely. Depending on the policy type, you may see Equal Parts Insurance Agency, LLC on your quote and policy documents or interact directly through their portal at https://www.equalparts.ai/."
        ]
      },
      {
        question: "What insurance can I get via Assurely?",
        answer: [
          "Via the Assurely platform, you can request all of the policies you need to run your business, including, E&O, BOP, cyber, general liability, property, auto and workers compensation. If you need a policy that was not listed above, don’t worry the Assurely team can get it for you. If you're unsure about your business insurance needs, licensed professionals are here to help. Contact them directly through the Assurely platform: https://client.assurely.com/partner/wingspan"
        ]
      },
      {
        question: "How do I access the Assurely platform?",
        answer: [
          "You can access the Assurely platform here: http://client.assurely.com/partner/wingspan. With your relationship with Wingspan, you already have a pre-made Assurely account. Considering how easy Assurely makes the entire insurance process for contractors, Wingspan wanted to make the signing up process as simple as possible. Once on the Assurely platform, you can easily apply for quotes in minutes, upload current insurance documents, or speak to a dedicated insurance advisor."
        ]
      },
      {
        question: "How much does insurance cost?",
        answer: [
          "Depending on your business and the coverage you need, the cost of insurance will vary. But rest assured, with Assurely, you will get the best coverage available at the best price possible. Assurely’s insurance advisors get quotes from all the top insurance carriers and present them to you with their expert recommendation on which is best for your business. As a result, you will know that someone has done the proper due diligence to ensure your insurance policies are cost effective and suited specifically for your unique business."
        ]
      },
      {
        question: "What is the claims process with Assurely?",
        answer: [
          "Assurely made the claims process simple, easy, and fast because they understand how big of a hassle it can turn into. In the event of a claim simply fill out a 2-minute form via assurely.com/claims, and your insurance advisor will get in touch with you."
        ]
      }
    ],
    sidebarLogoUrl:
      "https://cdn.prod.website-files.com/6143539d86d8a27256ea08db/68c0496e973f6a3ef9bec065_Assurely%20an%20Equal%20Parts%20Company%20(3).png",
    sidebarHideMemberIds: true,
    sidebarCtaLink: "https://client.assurely.com/partner/wingspan",
    sidebarCtaText: "Get covered",
    sidebarContactEmail: "service@equalparts.ai",
    sidebarContactPhone: "(478) 227-7873",
    isAssurely: true,
    customEnrollLink: "https://get.equalparts.ai/"
  },

  // ---------------------------------------------------------------------------
  // Meridio — gated on the `meridio` feature flag.
  // ---------------------------------------------------------------------------
  {
    slug: MERIDIO_KEY,
    name: "Health Insurance",
    category: BenefitCategory.Insurance,
    orderPriority: 0,
    gate: BenefitGate.MeridioFlag,
    isAdditionalCoverage: true,
    atAGlance:
      "Where health benefits meet everyday people. Telehealth available 24/7.",
    logoUrl: meridioSmallLogo,
    costDescription: "Plans start as low as $170/month",
    costIsNotIncluded: true,
    mainHeader: "Everyday Health",
    videoUrl: "https://player.vimeo.com/video/827040201?h=ae3c844ace",
    mainDescription:
      "Wingspan partners with Meridio to offer contractors access to healthcare benefits and assistance in navigating options from preventive to traditional medical, dental & vision coverage.",
    mainCards: [
      {
        icon: "heart",
        lines: ["Preventive Care and Competitive Health Coverage"]
      },
      { icon: "health", lines: ["Dental & Vision Coverage"] }
    ],
    mainFaqs: [
      {
        question:
          "Am I still eligible for benefits if I have not worked any hours?",
        answer: (
          <>
            Yes! Wingspan has partnered with Meridio to provide access to all
            contractors. Ensure that you have a default payment method by going
            to:
            <WSElement as="ul" ml="XL">
              <li>Settings</li>
              <li>Payout &amp; Payment Methods</li>
              <li>Add payment method</li>
              <li>Mark as the default payment method</li>
            </WSElement>
          </>
        )
      },
      {
        question: "What kind of health coverage does Meridio offer?",
        answer: [
          "Meridio offers preventive and competitive plans starting at $170/month per individual. Dental & Vision plans start at $30/month per individual."
        ]
      },
      {
        question: "Why choose Meridio?",
        answer: [
          "Meridio’s health plans are designed to support contractors offering guaranteed acceptance, standard rates in all 50 states, $0 deductibles, and reasonable premiums. With Meridio, you choose the benefits that are right for you and your household, and if you change careers, your coverage plan goes with you."
        ]
      },
      {
        question: "Where can I review plans?",
        answer: (
          <>
            You can review plan options by visiting{" "}
            <a href="https://base.getmeridio.com/b/ws-b?companydisplayname=Wingspan&cd=Q87LLCI6&phone=1-888-294-9181">
              HERE
            </a>
          </>
        )
      },
      {
        question: "Can I add my significant other?",
        answer: [
          "Yes, you can add your significant other, fiance, or domestic partner. Meridio also provides coverage options for children and family members."
        ]
      },
      {
        question: "Will my physician be in network?",
        answer: (
          <>
            Meridio utilizes the PHCS network through Multiplan which is the
            largest independent PPO network in the nation. You can visit{" "}
            <a href="https://www.multiplan.us/">multiplan.com</a> to search for
            your existing providers following the steps below:
            <WSElement as="ul" ml="XL">
              <li>
                Click “Find a Provider” located at the top right-hand corner
              </li>
              <li>Click “Select Network”</li>
              <li>
                Select “PHCS” Depending on the coverage type your interested in
                next steps will vary: <br />
                <b>Preventive:</b> MEC Ultimate → select “Preventive only” MEC
                Ultimate HI → select “Specifics” <br />
                <b>Competitive:</b> Both IHP plans → select “Practitioner &amp;
                Ancillary”
              </li>
            </WSElement>
          </>
        )
      },
      {
        question: "When is open enrollment?",
        answer: [
          "Open enrollment is year-round. If you’re looking for coverage as soon as the 1st of the following month, enroll by the 10th of the current month."
        ]
      },
      {
        question: "When do my benefits take effect?",
        answer: [
          "Depending on when you enroll, benefits take effect on the first of the following month."
        ]
      },
      {
        question: "When will I get my ID cards?",
        answer: [
          "Physical cards and packet will be mailed mid-month AFTER your coverage start date."
        ]
      },
      {
        question: "****To access your digital cards:",
        answer: (
          <WSElement as="ul" ml="XL">
            <li>Competitive (IHP) Plans: Refer to the guidebook</li>
            <li>
              Preventive (MEC) Plans: Email request to{" "}
              <a href="mailto:customercare@getmeridio.com">
                customercare@getmeridio.com
              </a>
            </li>
          </WSElement>
        )
      }
    ],
    sidebarHeader: "Helping you Work, Plan and Live Better.",
    sidebarLogoUrl: meridioHorizontalLogo,
    sidebarUsageText:
      "Review medical, dental & vision plans and schedule a needs assessment with a licensed Benefits Guide.",
    sidebarHideMemberIds: true,
    sidebarCtaCustom: <MeridioCTA />,
    enrolledPlanType: "MeridioHealth"
  },

  // ---------------------------------------------------------------------------
  // Wingspan master-policy insurance — gated on the master-policy flow.
  // ---------------------------------------------------------------------------
  {
    slug: OAI_KEY,
    name: oai.name,
    category: BenefitCategory.Insurance,
    orderPriority: 10,
    gate: BenefitGate.MasterPolicy,
    isAdditionalCoverage: true,
    atAGlance: "Your financial safety net for on-the-job accidents",
    icon: "shield-check-fill",
    iconColor: "white",
    iconBackgroundColor: "garnet",
    costDescription: `While enrolled, ${oai.monthlyPremiumFormatted} of your gross earnings are deducted from each payment processed through Wingspan`,
    costShortDescription: `${oai.monthlyPremiumFormatted} of gross income`,
    costIsNotIncluded: true,
    mainHeader: oai.name,
    sidebarHideMemberIds: true,
    isAssurely: true,
    enrolledPlanType: OAI_KEY
  },

  {
    slug: PGLI_KEY,
    name: pgli.name,
    category: BenefitCategory.Insurance,
    orderPriority: 11,
    gate: BenefitGate.MasterPolicy,
    isAdditionalCoverage: true,
    atAGlance:
      "A Professional Liability (Errors & Omissions) and General Liability policy for specific, eligible professional groups",
    icon: "shield-check-fill",
    iconColor: "white",
    iconBackgroundColor: "garnet",
    costDescription: `Cost: Only ${pgli.monthlyPremiumFormatted} per month + one time ${pgli.initiationFeeFormatted} setup fee.`,
    costShortDescription: `${pgli.monthlyPremiumFormatted}/mo + ${pgli.initiationFeeFormatted} setup fee`,
    costIsNotIncluded: true,
    mainHeader: pgli.name,
    sidebarHideMemberIds: true,
    isAssurely: true,
    enrolledPlanType: PGLI_KEY
  },

  // ---------------------------------------------------------------------------
  // Axis partner catalog — rendered on its own route.
  // ---------------------------------------------------------------------------
  {
    slug: "transamerica-final-expense",
    name: "Final Expense Coverage with Funeral Concierge",
    category: BenefitCategory.Insurance,
    orderPriority: 12,
    gate: BenefitGate.AxisPartner,
    isAdditionalCoverage: true,
    atAGlance:
      "Lifetime protection for final expenses plus 24/7 concierge funeral planning.",
    logoUrl: transamericaLogo,
    logoWidth: AXIS_LOGO_WIDTH,
    iconColor: "white",
    iconBackgroundColor: "garnet",
    hideCost: true,
    mainHeader: "Final Expense Coverage with Funeral Concierge",
    mainDescription:
      "Transamerica's Final Expense plan ensures your loved ones aren't burdened with funeral or end-of-life costs. Coverage is simple to apply for, and includes a built-in concierge service to guide your family through funeral planning and related arrangements.",
    mainFaqsHeader: "FAQs",
    mainFaqs: [
      {
        question: "What is Final Expense Insurance?",
        answer: ["A permanent life policy covering funeral and burial costs."]
      },
      {
        question: "Is there a medical exam?",
        answer: ["Often simplified or no-exam underwriting."]
      },
      {
        question: "What is the concierge service?",
        answer: [
          "A 24/7 support resource to help with funeral coordination and paperwork."
        ]
      }
    ],
    sidebarLogoUrl: transamericaLogo,
    sidebarHideMemberIds: true,
    ...AXIS_CONTACT
  },

  {
    slug: "mutual-trust-whole-life-iul",
    name: "Whole Life / Indexed Universal Life (IUL)",
    category: BenefitCategory.Insurance,
    orderPriority: 13,
    gate: BenefitGate.AxisPartner,
    isAdditionalCoverage: true,
    atAGlance:
      "Build cash value while securing lifelong protection and financial flexibility.",
    logoUrl: mutualTrustLogo,
    logoWidth: AXIS_LOGO_WIDTH,
    iconColor: "white",
    iconBackgroundColor: "garnet",
    hideCost: true,
    mainHeader: "Whole Life / Indexed Universal Life (IUL)",
    mainDescription:
      "Mutual Trust, a Pan-American Life company, offers permanent life insurance options designed to build long-term cash value. Whole life and IUL policies provide steady growth potential, access to policy loans, and lifetime protection—ideal for financial independence and future planning.",
    mainFaqsHeader: "FAQs",
    mainFaqs: [
      {
        question: "What is Whole Life Insurance?",
        answer: ["Permanent protection that builds guaranteed cash value."]
      },
      {
        question: "What is an IUL?",
        answer: [
          "A policy that links cash value growth to a market index while protecting from market loss."
        ]
      },
      {
        question: "Can I borrow from my policy?",
        answer: ["Yes, policy loans are available once value accumulates."]
      }
    ],
    sidebarLogoUrl: mutualTrustLogo,
    sidebarHideMemberIds: true,
    ...AXIS_CONTACT
  },

  {
    slug: "mutual-of-omaha-term-life",
    name: "Term Life (10, 15, 20, 30-Year)",
    category: BenefitCategory.Insurance,
    orderPriority: 14,
    gate: BenefitGate.AxisPartner,
    isAdditionalCoverage: true,
    atAGlance: "Affordable protection for the years you need it most.",
    logoUrl: mutualOfOmahaLogo,
    logoWidth: AXIS_LOGO_WIDTH,
    icon: "calendar-check-fill",
    iconColor: "white",
    iconBackgroundColor: "garnet",
    hideCost: true,
    mainHeader: "Term Life (10, 15, 20, 30-Year)",
    mainDescription:
      "Mutual of Omaha offers level-term life insurance designed to protect your income and family during key earning years. Choose 10-, 15-, 20-, or 30-year terms with level premiums and the option to convert to permanent coverage later on.",
    mainFaqsHeader: "FAQs",
    mainFaqs: [
      {
        question: "Why term life?",
        answer: ["Cost-effective coverage during high-need years."]
      },
      {
        question: "Can I extend or convert later?",
        answer: ["Yes, conversion options are available."]
      },
      {
        question: "Is the death benefit guaranteed?",
        answer: ["Yes, as long as premiums are paid on time."]
      }
    ],
    sidebarLogoUrl: mutualOfOmahaLogo,
    sidebarHideMemberIds: true,
    ...AXIS_CONTACT
  },

  {
    slug: "manhattan-life-hospital-indemnity",
    name: "Hospital Indemnity",
    category: BenefitCategory.Insurance,
    orderPriority: 15,
    gate: BenefitGate.AxisPartner,
    isAdditionalCoverage: true,
    atAGlance:
      "Cash benefits for unexpected hospital stays and recovery expenses.",
    logoUrl: manhattanLifeLogo,
    logoWidth: AXIS_LOGO_WIDTH,
    iconColor: "white",
    iconBackgroundColor: "garnet",
    hideCost: true,
    mainHeader: "Hospital Indemnity",
    mainDescription:
      "Manhattan Life's Hospital Indemnity plan pays cash directly to you to help with costs during hospital stays or outpatient recovery. Use the benefit however you choose—for bills, travel, or daily living expenses—and stay financially protected during health challenges.",
    mainFaqsHeader: "FAQs",
    mainFaqs: [
      {
        question: "Is this health insurance?",
        answer: ["No, it supplements your health coverage."]
      },
      {
        question: "How can the benefit be used?",
        answer: ["Funds can be used for any personal expense."]
      }
    ],
    sidebarLogoUrl: manhattanLifeLogo,
    sidebarHideMemberIds: true,
    ...AXIS_CONTACT
  },

  {
    slug: "united-healthcare",
    name: "Health Insurance – PPO, HMO, and Group Health Options",
    category: BenefitCategory.Health,
    orderPriority: 16,
    gate: BenefitGate.AxisPartner,
    isAdditionalCoverage: true,
    atAGlance:
      "Comprehensive medical coverage with flexible network choices for individuals and groups.",
    logoUrl: unitedHealthcareLogo,
    logoWidth: AXIS_LOGO_WIDTH,
    iconColor: "white",
    iconBackgroundColor: "garnet",
    hideCost: true,
    mainHeader: "Health Insurance – PPO, HMO, and Group Health Options",
    mainDescription:
      "UnitedHealthcare offers a range of health plans designed to fit diverse needs and budgets—whether individual PPO and HMO coverage or group health options for organizations. Plans include access to one of the nation's largest provider networks, preventive care, prescription benefits, and virtual health services.",
    mainFaqsHeader: "FAQs",
    mainFaqs: [
      {
        question: "What's the difference between PPO and HMO?",
        answer: [
          "PPO plans offer more provider flexibility, while HMOs are typically lower-cost with coordinated care."
        ]
      },
      {
        question: "Are preventive services covered?",
        answer: [
          "Yes, annual checkups and screenings are generally included at no additional cost."
        ]
      },
      {
        question: "Can this be offered to groups?",
        answer: [
          "Yes, group health options are available for organizations and their independent contractors."
        ]
      }
    ],
    sidebarLogoUrl: unitedHealthcareLogo,
    sidebarHideMemberIds: true,
    ...AXIS_CONTACT
  },

  {
    slug: "bcmg-retirement-investment",
    name: "Retirement, Investment, and Wealth Management",
    category: BenefitCategory.Finances,
    orderPriority: 17,
    gate: BenefitGate.AxisPartner,
    isAdditionalCoverage: true,
    atAGlance:
      "Simplified investing and retirement planning through BCMG, a registered investment advisor.",
    logoUrl: bcmgLogo,
    logoWidth: AXIS_LOGO_WIDTH,
    iconColor: "white",
    iconBackgroundColor: "garnet",
    hideCost: true,
    mainHeader: "Retirement, Investment, and Wealth Management",
    mainDescription:
      "Through BCMG, a registered investment advisor, contractors and companies gain access to modern retirement and investment tools. Advisors help clients plan, invest, and build long-term financial security through an integrated digital platform.",
    mainFaqsHeader: "FAQs",
    mainFaqs: [
      {
        question: "Who manages the investments?",
        answer: ["Licensed advisors under Blasen Capital Markets Group."]
      },
      {
        question: "What accounts are available?",
        answer: ["IRAs, 401ks, brokerage, and managed portfolios."]
      },
      {
        question: "Is there a minimum investment?",
        answer: ["Account minimums vary."]
      }
    ],
    sidebarLogoUrl: bcmgLogo,
    sidebarHideMemberIds: true,
    ...AXIS_CONTACT
  },

  {
    slug: "topmark-equipment-financing",
    name: "Equipment Financing Quote",
    category: BenefitCategory.Finances,
    orderPriority: 18,
    gate: BenefitGate.AxisPartner,
    isAdditionalCoverage: true,
    atAGlance: "Streamlined Business, Truck, and Equipment Financing",
    logoUrl: lloydCapitalLogo,
    logoWidth: AXIS_LOGO_WIDTH,
    iconColor: "white",
    iconBackgroundColor: "garnet",
    hideCost: true,
    mainHeader: "Equipment Financing Quote",
    mainDescription:
      "Business loans for trucks and equipment, plus lines of credit and cash advance options.",
    mainLearnMoreLink:
      "https://www.topmarkfunding.com/lloyd-capital-financing-quote/",
    mainLearnMoreLinkText: "Learn more",
    sidebarLogoUrl: lloydCapitalLogo,
    sidebarLogoPlaceholder: "LLOYD CAPITAL",
    sidebarHideMemberIds: true,
    ...AXIS_CONTACT,
    sidebarContactDescription: "Contact: Kylie Blasen"
  }
];

export const BENEFITS_BY_SLUG: Record<string, Benefit> = BENEFITS.reduce(
  (acc, benefit) => ({ ...acc, [benefit.slug]: benefit }),
  {} as Record<string, Benefit>
);
